import React, { useState } from 'react';
import { apiAdmin, setSuperAdminToken, getDisplayMessage } from '@/api/apiClient';
import { Link2, Search, Loader2 } from 'lucide-react';

const DEFAULT_EVIDENCE_ID = '26c75b64-632a-4c7a-805c-408490ee0595';

export default function AdminCertifyGDriveLink() {
  const [evidenceId, setEvidenceId] = useState(DEFAULT_EVIDENCE_ID);
  const [gdriveFileId, setGdriveFileId] = useState('');
  const [autoFind, setAutoFind] = useState(true);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [erro, setErro] = useState('');
  const [success, setSuccess] = useState('');
  const [hint, setHint] = useState('');

  const consultar = async () => {
    const id = evidenceId?.trim();
    if (!id) return;
    setLoading(true);
    setErro('');
    setSuccess('');
    setHint('');
    setStatus(null);
    try {
      const data = await apiAdmin.get(`/api/admin/certify/evidences/${encodeURIComponent(id)}/storage`);
      setStatus(data);
    } catch (err) {
      if (err?.status === 401) {
        setSuperAdminToken(null);
        window.location.href = '/admin';
        return;
      }
      setErro(getDisplayMessage(err, 'Erro ao consultar evidência.'));
    } finally {
      setLoading(false);
    }
  };

  const ligar = async () => {
    const id = evidenceId?.trim();
    if (!id) return;
    setLinking(true);
    setErro('');
    setSuccess('');
    setHint('');
    try {
      const fileInput = gdriveFileId?.trim() ?? '';
      const isFolderUrl = /\/folders\//i.test(fileInput);
      const body = { auto_find: autoFind };
      if (fileInput && !isFolderUrl) {
        body.gdrive_file_id = fileInput;
      }
      const data = await apiAdmin.post(
        `/api/admin/certify/evidences/${encodeURIComponent(id)}/link-gdrive`,
        body
      );
      if (data?.ok) {
        setSuccess(
          `Ligado ao Drive: ${data.gdrive_file_name} (${data.gdrive_file_id})${
            data.auto_found ? ' — encontrado automaticamente na pasta' : ''
          }. Download público: ${data.file_download_available ? 'disponível' : 'indisponível — verifique partilha com a service account'}.`
        );
        await consultar();
      } else {
        setErro(data?.error || 'Falha ao ligar.');
        if (data?.hint) setHint(data.hint);
      }
    } catch (err) {
      const body = err?.data;
      setErro(body?.error || getDisplayMessage(err, 'Falha ao ligar ao Google Drive.'));
      if (body?.hint) setHint(body.hint);
    } finally {
      setLinking(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-semibold text-slate-800 mb-2">Certify — Ligar ficheiro no Google Drive</h1>
      <p className="text-sm text-slate-600 mb-6">
        Associa um ficheiro já existente na pasta do Drive ao registo da evidência, para o link público{' '}
        <code className="text-xs">/verify/…</code> voltar a permitir descarga (mesma senha de verificação).
      </p>

      {erro && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-800 text-sm border border-red-200 space-y-1">
          <p>{erro}</p>
          {hint && <p className="text-red-900/90">{hint}</p>}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-800 text-sm border border-green-200">
          {success}
        </div>
      )}

      <div className="space-y-4 bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <div>
          <label htmlFor="evidenceId" className="block text-sm font-medium text-slate-700 mb-1">
            ID da evidência (UUID)
          </label>
          <input
            id="evidenceId"
            value={evidenceId}
            onChange={(e) => setEvidenceId(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
            placeholder="26c75b64-632a-4c7a-805c-408490ee0595"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={consultar}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Consultar estado
          </button>
        </div>

        {status && (
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm space-y-2">
            <p>
              <span className="font-medium">Título:</span> {status.titulo}
            </p>
            <p>
              <span className="font-medium">Ficheiro:</span> {status.file_name}
            </p>
            <p>
              <span className="font-medium">Senha verify:</span>{' '}
              <code className="font-mono">{status.public_password}</code>
            </p>
            <p>
              <span className="font-medium">Upload blob:</span>{' '}
              <code className="text-xs break-all">{status.upload_blob_id || '—'}</code>
            </p>
            <p>
              <span className="font-medium">Armazenamento:</span> {status.storage_mode || '—'}{' '}
              {status.gdrive_file_id ? (
                <>
                  · Drive ID: <code className="text-xs">{status.gdrive_file_id}</code>
                </>
              ) : null}
            </p>
            <p>
              <span className="font-medium">Download público:</span>{' '}
              {status.file_download_available ? (
                <span className="text-green-700">disponível</span>
              ) : (
                <span className="text-amber-700">{status.unavailable_reason || 'indisponível'}</span>
              )}
            </p>
            {!status.gdrive_configured && (
              <p className="text-amber-800">
                Google Drive não está configurado no servidor. Configure em Config. Google Drive primeiro.
              </p>
            )}
          </div>
        )}

        <hr className="border-slate-200" />

        <div>
          <label className="flex items-center gap-2 text-sm text-slate-700 mb-3">
            <input
              type="checkbox"
              checked={autoFind}
              onChange={(e) => setAutoFind(e.target.checked)}
              className="rounded"
            />
            Procurar automaticamente na pasta (nome <code className="text-xs">{'{upload_blob_id}_'}</code>…)
          </label>
          <label htmlFor="gdriveFileId" className="block text-sm font-medium text-slate-700 mb-1">
            ID do ficheiro no Drive (opcional)
          </label>
          <input
            id="gdriveFileId"
            value={gdriveFileId}
            onChange={(e) => setGdriveFileId(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
            placeholder="https://drive.google.com/file/d/XXXX/view — não use URL /folders/…"
          />
          {gdriveFileId?.trim() && /\/folders\//i.test(gdriveFileId) && (
            <p className="text-xs text-amber-700 mt-1">
              Isto é uma URL de <strong>pasta</strong>. Deixe este campo vazio e use «Procurar automaticamente», ou abra o
              ficheiro dentro da pasta e copie a URL com <code className="text-[11px]">/file/d/</code>.
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={ligar}
          disabled={linking}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-medium hover:bg-slate-700 disabled:opacity-50"
        >
          {linking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
          Ligar ao Google Drive
        </button>
      </div>

      <p className="mt-6 text-xs text-slate-500">
        Após ligar, teste{' '}
        <a
          href={`https://auction.elicitacao.cloud/verify/${evidenceId.trim() || DEFAULT_EVIDENCE_ID}`}
          className="text-primary underline"
          target="_blank"
          rel="noreferrer"
        >
          o link público de verificação
        </a>{' '}
        com a senha indicada acima.
      </p>
    </div>
  );
}
